import { angleDiff, collidesWalls, dist, inLight, raycastHit } from '../lib/geometry.js';
import { aStar, smoothPath, worldToTile } from '../lib/pathfinding.js';
import type { Guard, Vec2, World } from '../world/types.js';

// Chase speed has to outpace the player's base 150 px/s — otherwise the
// chase is a non-event because you can just hold a direction and win.
// Forcing the player below guard speed means the only escape is to
// break line of sight (a wall, a corner) and hide.
const SPD_CHASE = 170;
const SPD_SUSPICIOUS = 75;
const SPD_INVESTIGATE = 65;
const SPD_PATROL = 50;
const ALERT_RAMP_UP = 90;
// Slower alert ramp when the guard *only* notices a moving box, not the
// player directly. Roughly 1/3 of the direct-sight rate so the box still
// works as cover as long as the player keeps it modest.
const ALERT_RAMP_BOX = 35;
const ALERT_DECAY = 18;
const CATCH_RADIUS = 16;
// A moving box is "noticed" only if its speed exceeds this threshold.
// Below it, the box is treated as stationary and the player is invisible.
const BOX_NOTICE_SPEED = 8;
// Beyond this fraction of vision, a box in shadow is too far away to be
// noticed. Lets the player slip a moving box past from a distance if
// they stick to the dark.
const BOX_NOTICE_SHADOW_FRAC = 0.4;
// Point-blank notice: a moving box within this distance of a guard is
// noticed regardless of vision cone — the guard hears/feels it bump
// into them. Only requires the line of sight not to pass through a wall
// (so the box behind a wall right next to a guard is still safe).
const BOX_PROXIMITY_NOTICE = 35;
// Lower speed threshold for the proximity rule. Even slow movement at
// point-blank is suspicious; only true zero-speed (parked box) hides.
const BOX_PROXIMITY_SPEED = 2;
// An alerted guard who reaches this close to a hidden player kicks the
// box → discovery → game over.
const BOX_DISCOVERY_RADIUS = 22;
const WAYPOINT_REACH_RADIUS = 11;
const STUCK_THRESHOLD_S = 0.3;
const TARGET_REACH_RADIUS = 7;
// Maximum turn rate (rad/s). Above the orbit-critical value
// `spd / turnRate`, a steering agent can track a point smaller than the
// arrival radius — below it, the agent endlessly orbits. We pick rates
// that put the critical radius safely under TARGET_REACH_RADIUS for
// every state speed.
const TURN_RATE_NORMAL = 9;
const TURN_RATE_CHASE = 14;
// Distance from current waypoint at which we start blending the aim toward
// the *next* waypoint, for smoother corner cuts.
const LOOKAHEAD_BLEND_RADIUS = WAYPOINT_REACH_RADIUS * 1.6;

interface Perceived {
  x: number;
  y: number;
}

/** True when the guard has direct line of sight on a *non-boxed* player.
 * Light/shadow + cone + vision range + walls all apply. The boxed-player
 * case is handled separately by isBoxNoticed. */
function isPlayerDirectlyVisible(world: World, g: Guard): boolean {
  const { player } = world;
  if (player.inBox) return false;
  if (!player.hp) return false;

  const dx = player.x - g.x;
  const dy = player.y - g.y;
  const d = Math.hypot(dx, dy);
  if (d > g.vision) return false;

  const ang = Math.atan2(dy, dx);
  if (Math.abs(angleDiff(ang, g.dir)) > g.fov / 2) return false;

  if (raycastHit(world, g.x, g.y, player.x, player.y)) return false;

  // Distant + in shadow → flaky detection (gives stealth play room)
  if (!inLight(world, player) && d > g.vision * 0.55) {
    return Math.random() > 0.4;
  }
  return true;
}

/**
 * True when the guard becomes aware of the player hidden inside a moving
 * box. Two paths to detection:
 *
 *  - **Proximity** (any direction): a box bumping into a guard at
 *    point-blank is noticed even if the guard is facing the other way.
 *    Only blocked by an actual wall between them. A truly stationary
 *    box still hides — you have to keep your nerve and stop when they
 *    get close.
 *
 *  - **Vision cone**: at longer range, the standard cone + light/shadow
 *    rules apply. Shadow shortens the notice range so the player can
 *    sneak a moving box past in dim corners.
 */
function isBoxNoticed(world: World, g: Guard): boolean {
  const { player } = world;
  if (!player.inBox) return false;
  if (!player.hp) return false;

  const speed = Math.hypot(player.vx, player.vy);
  const dx = player.x - g.x;
  const dy = player.y - g.y;
  const d = Math.hypot(dx, dy);

  // Point-blank: any non-trivial movement gets noticed in any direction.
  if (
    d < BOX_PROXIMITY_NOTICE &&
    speed > BOX_PROXIMITY_SPEED &&
    !raycastHit(world, g.x, g.y, player.x, player.y)
  ) {
    return true;
  }

  // Standard cone-based detection further out.
  if (speed < BOX_NOTICE_SPEED) return false;
  if (d > g.vision) return false;

  const ang = Math.atan2(dy, dx);
  if (Math.abs(angleDiff(ang, g.dir)) > g.fov / 2) return false;

  if (raycastHit(world, g.x, g.y, player.x, player.y)) return false;

  if (!inLight(world, player) && d > g.vision * BOX_NOTICE_SHADOW_FRAC) {
    return false;
  }
  return true;
}

export function updateGuards(world: World, dt: number, onLose: () => void): void {
  for (const g of world.guards) {
    if (g.ko) continue;
    updateGuard(world, g, dt, onLose);
  }
}

function updateGuard(world: World, g: Guard, dt: number, onLose: () => void): void {
  const { player, sounds } = world;

  // Reset to 0 — moveAlongPath will overwrite if the guard actually
  // travelled this tick. Without this, a guard that just reached its
  // waypoint and skipped the movement branch would keep its previous
  // currentSpeed and the renderer would still show the walking pose.
  g.currentSpeed = 0;

  let perceived: Perceived | null = null;
  if (isPlayerDirectlyVisible(world, g)) {
    perceived = { x: player.x, y: player.y };
  }
  // Box noticed = the player is in a moving box and the box just passed
  // through the cone. Triggers suspicion at a slower ramp than direct sight.
  const boxNoticed = perceived === null && isBoxNoticed(world, g);

  // Pick the strongest audible sound (closest within radius, with partial wall bypass)
  let bestSoundDist = Infinity;
  let bestSound: Perceived | null = null;
  for (const s of sounds) {
    const dd = dist(g, s);
    if (dd < s.radius && (!raycastHit(world, g.x, g.y, s.x, s.y) || dd < s.radius * 0.6)) {
      if (dd < bestSoundDist) {
        bestSoundDist = dd;
        bestSound = { x: s.x, y: s.y };
      }
    }
  }

  if (perceived) {
    g.alert = Math.min(100, g.alert + ALERT_RAMP_UP * dt);
    g.lastSeen = perceived;
    if (g.alert >= 100) {
      g.state = 'chase';
      g.target = { x: perceived.x, y: perceived.y };
    } else {
      g.state = 'suspicious';
      g.target = { x: perceived.x, y: perceived.y };
    }
  } else if (boxNoticed) {
    // A moving box is suspect, not a confirmed sighting — slower ramp,
    // and we never escalate past `investigate` from this branch alone.
    g.alert = Math.min(100, g.alert + ALERT_RAMP_BOX * dt);
    g.lastSeen = { x: player.x, y: player.y };
    g.target = { x: player.x, y: player.y };
    g.state = g.alert >= 100 ? 'investigate' : 'suspicious';
  } else {
    g.alert = Math.max(0, g.alert - ALERT_DECAY * dt);
    if (bestSound && g.state !== 'chase') {
      g.state = 'investigate';
      g.target = { x: bestSound.x, y: bestSound.y };
      g.idleT = 0;
    } else if (g.state === 'chase' && g.lastSeen) {
      g.target = g.lastSeen;
      if (dist(g, g.lastSeen) < 8) {
        g.state = 'investigate';
        g.lastSeen = null;
        g.idleT = 0;
      }
    } else if ((g.state === 'investigate' || g.state === 'suspicious') && g.target) {
      if (dist(g, g.target) < 6) {
        g.idleT += dt;
        if (g.idleT > 1.4) {
          g.state = 'patrol';
          g.idleT = 0;
        }
      }
    }
  }

  // Resolve current movement target
  let spd: number;
  if (g.state === 'chase') spd = SPD_CHASE;
  else if (g.state === 'suspicious') spd = SPD_SUSPICIOUS;
  else if (g.state === 'investigate') spd = SPD_INVESTIGATE;
  else spd = SPD_PATROL;

  const tgt: Vec2 | null = g.state === 'patrol' ? g.path[g.pi]! : g.target;

  if (tgt) {
    moveAlongPath(world, g, tgt, spd, dt);
  } else {
    g.navPath = null;
    g.navTargetTile = null;
    g.navStuckT = 0;
  }

  // Catch the player
  if (g.state === 'chase' && !player.inBox && dist(g, player) < CATCH_RADIUS) {
    onLose();
  }
  // Box discovery — an alerted guard who reaches the box bumps into it
  // and uncovers the trick. Patrol guards walk past oblivious; only
  // guards in suspicious/investigate/chase trigger this.
  const alertedStates =
    g.state === 'suspicious' || g.state === 'investigate' || g.state === 'chase';
  if (player.inBox && alertedStates && dist(g, player) < BOX_DISCOVERY_RADIUS) {
    onLose();
  }
}

function moveAlongPath(world: World, g: Guard, tgt: Vec2, spd: number, dt: number): void {
  // === Plan / re-plan ===
  const tgtTile = worldToTile(world, tgt);
  const needReplan =
    !g.navPath ||
    !g.navTargetTile ||
    g.navTargetTile.tx !== tgtTile.tx ||
    g.navTargetTile.ty !== tgtTile.ty ||
    g.navStuckT > STUCK_THRESHOLD_S ||
    g.navIdx >= (g.navPath?.length ?? 0);

  if (needReplan) {
    const raw = aStar(world, g, tgt);
    g.navPath = raw ? smoothPath(world, raw) : null;
    g.navTargetTile = tgtTile;
    g.navStuckT = 0;
    // path[0] is the guard's start-tile center — A* starts there by
    // construction. The guard usually overshoots that tile center already
    // (its sub-tile position can be slightly past it), so aiming at path[0]
    // would cause a U-turn. Skip directly to path[1] when it exists.
    g.navIdx = g.navPath && g.navPath.length > 1 ? 1 : 0;
  }

  // === Pick the next aim point: the next un-reached waypoint, or the
  // raw target if pathfinding failed (fallback). ===
  let aim: Vec2 = tgt;
  if (g.navPath && g.navIdx < g.navPath.length) {
    while (
      g.navIdx < g.navPath.length - 1 &&
      dist(g.navPath[g.navIdx]!, g) < WAYPOINT_REACH_RADIUS
    ) {
      g.navIdx++;
    }
    aim = g.navPath[g.navIdx]!;

    // Look-ahead — when we get close to the current waypoint, blend the aim
    // toward the *next* one so we begin turning before fully arriving.
    // Smooths out the staircase corners left by the grid-based A*.
    if (g.navIdx < g.navPath.length - 1) {
      const next = g.navPath[g.navIdx + 1]!;
      const dToCurrent = dist(aim, g);
      if (dToCurrent < LOOKAHEAD_BLEND_RADIUS) {
        const blend = 1 - dToCurrent / LOOKAHEAD_BLEND_RADIUS;
        aim = {
          x: aim.x * (1 - blend) + next.x * blend,
          y: aim.y * (1 - blend) + next.y * blend,
        };
      }
    }
  }

  // Arrival at the *real* target (not just a waypoint) — let the state
  // machine in updateGuard's perception block transition the guard.
  const dToTgt = dist(g, tgt);
  if (dToTgt <= TARGET_REACH_RADIUS) {
    if (g.state === 'patrol') {
      g.pi = (g.pi + 1) % g.path.length;
      g.navPath = null;
      g.navTargetTile = null;
    }
    return;
  }

  // === Steer toward `aim` with a turn-rate cap. ===
  const dx = aim.x - g.x;
  const dy = aim.y - g.y;
  const targetDir = Math.atan2(dy, dx);
  const ad = angleDiff(targetDir, g.dir);
  const turnRate = g.state === 'chase' ? TURN_RATE_CHASE : TURN_RATE_NORMAL;
  g.dir += Math.max(-turnRate * dt, Math.min(turnRate * dt, ad));

  const stepX = Math.cos(g.dir) * spd * dt;
  const stepY = Math.sin(g.dir) * spd * dt;
  const tryX = g.x + stepX;
  const tryY = g.y + stepY;

  const beforeX = g.x;
  const beforeY = g.y;
  if (!collidesWalls(world, tryX, g.y, 11)) g.x = tryX;
  if (!collidesWalls(world, g.x, tryY, 11)) g.y = tryY;

  // Stuck detector — if we barely moved despite trying, bump the replan
  // timer so we re-route on the next frame.
  const moved = Math.hypot(g.x - beforeX, g.y - beforeY);
  if (moved < spd * dt * 0.3) g.navStuckT += dt;
  else g.navStuckT = 0;

  // Walking animation accumulator (used by the renderer).
  const speed = moved / Math.max(dt, 0.001);
  g.currentSpeed = speed;
  if (speed > 8) g.walkT += dt * (speed / 30);
}
